import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Fingerprint, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { registerPasskey, passkeysSupported, passkeyErrorMessage } from "@/lib/passkeys";

const SNOOZE_KEY = "passkey_prompt_snooze_until";
export const OFFER_KEY = "passkey_offer_pending";

// Shown once after a password sign-in when the user has no passkey yet
export function PasskeyPrompt() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(OFFER_KEY) !== "1") return;
    sessionStorage.removeItem(OFFER_KEY);
    if (!passkeysSupported() || Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now()) return;
    client.get("/auth/passkeys").then(({ data }) => { if (!data.length) setShow(true); }).catch(() => {});
  }, []);

  if (!show) return null;
  const snooze = () => { localStorage.setItem(SNOOZE_KEY, String(Date.now() + 14 * 86400000)); setShow(false); };
  const add = async () => {
    setBusy(true);
    try {
      if (await registerPasskey()) { toast.success("Passkey added. Next time just tap Sign in with passkey."); setShow(false); }
    } catch (e) {
      const msg = passkeyErrorMessage(e, formatApiErrorDetail);
      if (msg) toast.error(msg); else toast.message("Passkey setup cancelled");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-24 left-5 z-50 w-[22rem] max-w-[calc(100vw-2.5rem)] rounded-xl border border-sky-100 bg-white p-4 shadow-xl animate-in fade-in slide-in-from-bottom-4 md:bottom-5 lg:left-[17.25rem]" data-testid="passkey-prompt">
      <button onClick={snooze} className="absolute right-2.5 top-2.5 rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="passkey-prompt-close"><X className="h-4 w-4" /></button>
      <div className="flex gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><Fingerprint className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold text-slate-900">Add a passkey for faster sign-in</p>
          <p className="mt-0.5 text-xs text-slate-500">Next time, sign in with Face ID, Touch ID or Windows Hello. No password or 2FA code needed.</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={add} disabled={busy} className="gap-1.5 bg-sky-600 hover:bg-sky-700" data-testid="passkey-prompt-add">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Fingerprint className="h-3.5 w-3.5" />} Add passkey
            </Button>
            <Button size="sm" variant="ghost" onClick={snooze} data-testid="passkey-prompt-later">Not now</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
